import { getFinancialYearLabels } from "../../brsrFinancialYearLabels";
import { SHEET_NAME as PENALTY_OR_FINE_SHEET } from "./penaltyOrFineSheet";
import { SHEET_NAME as SETTLEMENT_SHEET } from "./settlementSheet";
import { SHEET_NAME as COMPOUNDING_FEE_SHEET } from "./compoundingFeeSheet";
import { SHEET_NAME as IMPRISONMENT_SHEET } from "./imprisonmentSheet";
import { SHEET_NAME as PUNISHMENT_SHEET } from "./punishmentSheet";
import { SHEET_NAME as APPEAL_OR_REVISION_CASE_SHEET } from "./appealOrRevisionCaseSheet";
import { SHEET_NAME as PROGRAMMES_FOR_VALUE_CHAIN_SHEET } from "./programmesForValueChainSheet";

const HEADER_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFB9D1EA" },
};

const SUBHEADER_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFD9D9D9" },
};

const TABLE_HEADER_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFFCE4D6" },
};

const THIN_BLACK = {
  top: { style: "thin", color: { argb: "FF000000" } },
  left: { style: "thin", color: { argb: "FF000000" } },
  bottom: { style: "thin", color: { argb: "FF000000" } },
  right: { style: "thin", color: { argb: "FF000000" } },
};

const BODY_FONT = { name: "Calibri", size: 11 };
const LINK_FONT = {
  name: "Calibri",
  size: 11,
  color: { argb: "FF0563C1" },
  underline: true,
};

const PRINCIPLE_TITLE =
  "Businesses should conduct and govern themselves with integrity, and in a manner that is Ethical, Transparent and Accountable.";

const COL_D = 4;
const COL_E = 5;
const COL_F = 6;
const COL_G = 7;
const COL_H = 8;
const COL_I = 9;
const COL_L = 12;
const COL_END = COL_L;

const LINE_HEIGHT = 15;
const MIN_ROW_HEIGHT = 18;

function borderCell(cell) {
  cell.border = THIN_BLACK;
}

function estimateRowHeight(text, colWidth, minHeight = MIN_ROW_HEIGHT) {
  const str = String(text ?? "");
  if (!str.trim()) return minHeight;
  const charsPerLine = Math.max(8, Math.floor(colWidth * 0.85));
  const lineCount = str.split(/\r?\n/).reduce(
    (total, part) => total + Math.max(1, Math.ceil(part.length / charsPerLine)),
    0
  );
  return Math.max(minHeight, lineCount * LINE_HEIGHT + 8);
}

function setAutoRowHeight(ws, row, parts) {
  ws.getRow(row).height = parts.reduce(
    (max, { text, width }) => Math.max(max, estimateRowHeight(text, width)),
    MIN_ROW_HEIGHT
  );
}

function setCell(ws, row, col, value, options = {}) {
  const cell = ws.getCell(row, col);
  if (options.link) {
    cell.value = { text: value || "View Details", hyperlink: options.link };
    cell.font = LINK_FONT;
  } else {
    cell.value = value;
    cell.font = options.bold ? { ...BODY_FONT, bold: true } : BODY_FONT;
  }
  borderCell(cell);
  cell.alignment = {
    horizontal: options.align ?? (options.link ? "center" : "left"),
    vertical: options.vertical || "middle",
    wrapText: true,
  };
  if (options.fill) cell.fill = options.fill;
}

function mergeRow(ws, row, startCol, endCol, value, options = {}) {
  if (startCol < endCol) ws.mergeCells(row, startCol, row, endCol);
  setCell(ws, row, startCol, value, options);
  for (let col = startCol + 1; col <= endCol; col++) {
    borderCell(ws.getCell(row, col));
  }
}

function safeText(value, fallback = "-") {
  if (value === null || value === undefined || value === "") return fallback;
  if (Array.isArray(value)) return value.length ? value.join(", ") : fallback;
  if (typeof value === "object") return fallback;
  return String(value);
}

function toYesNo(value) {
  if (value === true || value === "true" || value === "yes" || value === "Yes") return "Yes";
  if (value === false || value === "false" || value === "no" || value === "No") return "No";
  return "-";
}

function getNumberOrNA(value) {
  return value === 0 ? "0" : safeText(value, "N/A");
}

function formatPercent(value) {
  if (value === null || value === undefined || value === "") return "-";
  const str = String(value);
  return str.includes("%") ? str : `${str}%`;
}

function topicsText(value) {
  if (Array.isArray(value)) {
    const filtered = value.filter(
      (item) => String(item).trim().toLowerCase() !== "select all"
    );
    return filtered.length ? filtered.join(", ") : "-";
  }
  return safeText(value);
}

function disciplinaryValue(obj) {
  if (obj && typeof obj === "object") {
    return {
      current: getNumberOrNA(obj.currentYear),
      previous: getNumberOrNA(obj.previousYear),
    };
  }
  return { current: getNumberOrNA(obj), previous: "N/A" };
}

function rptValue(rpt, section, key, yearKey) {
  return getNumberOrNA(rpt?.[section]?.[key]?.[yearKey]);
}

const OPENNESS_ROWS = [
  {
    parameter: "Concentration of Purchases",
    metric: "a. Purchases from trading houses as % of total purchases",
    section: "concentrationOfPurchased",
    key: "purchasedFromTrading",
  },
  {
    parameter: "Concentration of Purchases",
    metric: "b. Number of trading houses where purchases are made from",
    section: "concentrationOfPurchased",
    key: "purchasedAreMadeFrom",
  },
  {
    parameter: "Concentration of Purchases",
    metric:
      "c. Purchases from top 10 trading houses as % of total purchases from trading houses",
    section: "concentrationOfPurchased",
    key: "top10TradingHouse",
  },
  {
    parameter: "Concentration of Sales",
    metric: "a. Sales to dealers / distributors as % of total sales",
    section: "concentrationOfSales",
    key: "salesOfDealerDistributors",
  },
  {
    parameter: "Concentration of Sales",
    metric: "b. Number of dealers / distributors to whom sales are made",
    section: "concentrationOfSales",
    key: "salesAreMade",
  },
  {
    parameter: "Concentration of Sales",
    metric:
      "c. Sales to top 10 dealers / distributors as % of total sales to dealers / distributors",
    section: "concentrationOfSales",
    key: "top10dealers",
  },
  {
    parameter: "Share of RPTs in",
    metric: "a. Purchases (Purchases with related parties / Total Purchases)",
    section: "shareOfRPTs",
    key: "purchases",
  },
  {
    parameter: "Share of RPTs in",
    metric: "b. Sales (Sales to related parties / Total Sales)",
    section: "shareOfRPTs",
    key: "sales",
  },
  {
    parameter: "Share of RPTs in",
    metric:
      "c. Loans & advances (Loans & advances given to related parties / Total loans & advances)",
    section: "shareOfRPTs",
    key: "loans",
  },
  {
    parameter: "Share of RPTs in",
    metric: "d. Investments (Investments in related parties / Total Investments made)",
    section: "shareOfRPTs",
    key: "investments",
  },
];

function addSectionHeader(ws, row, title, fill = HEADER_FILL) {
  mergeRow(ws, row, COL_D, COL_END, title, {
    bold: true,
    align: "center",
    fill,
  });
  setAutoRowHeight(ws, row, [{ text: title, width: 90 }]);
  return row + 1;
}

function addQuestionRow(ws, row, label, value, options = {}) {
  if (options.fullWidth || (value === "" && !options.valueCol)) {
    mergeRow(ws, row, COL_D, COL_END, label, {
      align: "left",
      vertical: "top",
      bold: options.bold,
    });
    setAutoRowHeight(ws, row, [{ text: label, width: 90 }]);
    return row + 1;
  }

  const valueCol = options.valueCol || COL_E;
  const valueEnd = options.valueEnd || COL_END;
  mergeRow(ws, row, COL_D, valueCol - 1, label, {
    align: "left",
    vertical: "top",
    bold: options.bold,
  });
  if (valueCol <= valueEnd) {
    mergeRow(ws, row, valueCol, valueEnd, value ?? "", {
      align: options.valueAlign ?? (options.link ? "center" : "left"),
      vertical: "top",
      link: options.link,
    });
  }
  setAutoRowHeight(ws, row, [
    { text: label, width: 28 },
    { text: value ?? "", width: 62 },
  ]);
  return row + 1;
}

function addTrainingTable(ws, row, training) {
  row = addQuestionRow(
    ws,
    row,
    "1. Percentage coverage by training and awareness programmes on any of the Principles during the financial year:",
    "",
    { fullWidth: true }
  );

  setCell(ws, row, COL_D, "Segment", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_E, "Total number of training and awareness programmes held", {
    bold: true,
    fill: TABLE_HEADER_FILL,
    align: "center",
  });
  mergeRow(
    ws,
    row,
    COL_F,
    COL_G,
    "Topics/principles covered under the training and its impact",
    { bold: true, fill: TABLE_HEADER_FILL, align: "center" }
  );
  mergeRow(
    ws,
    row,
    COL_H,
    COL_I,
    "%age of persons in respective category covered by the awareness programmes",
    { bold: true, fill: TABLE_HEADER_FILL, align: "center" }
  );
  row += 1;

  const segments = [
    ["boardOfDirectors", "Board of Directors"],
    ["keyManagementPersonnel", "Key Management Personnel"],
    ["employeesOtherThanBoDAndKMPs", "Employees other than BoD and KMPs"],
    ["workers", "Workers"],
  ];

  segments.forEach(([key, label]) => {
    const item = training?.[key] || {};
    setCell(ws, row, COL_D, label, { align: "left", vertical: "top" });
    setCell(
      ws,
      row,
      COL_E,
      safeText(item.awarenessProgramHeld ?? item.totalProgrammesHeld),
      { align: "center" }
    );
    mergeRow(ws, row, COL_F, COL_G, topicsText(item.principleUnderTraining), {
      align: "left",
      vertical: "top",
    });
    mergeRow(
      ws,
      row,
      COL_H,
      COL_I,
      formatPercent(item.categoryCoveredPercentage ?? item.percentageCovered),
      { align: "center" }
    );
    setAutoRowHeight(ws, row, [
      { text: label, width: 22 },
      { text: topicsText(item.principleUnderTraining), width: 24 },
    ]);
    row += 1;
  });

  return row;
}

function addFineRows(ws, row, essential) {
  const showDetails = !!essential?.detailsOfFine;

  row = addQuestionRow(
    ws,
    row,
    "2. Details of fines / penalties / punishment / award / compounding fees / settlement amount paid in proceedings (by the entity or by directors / KMPs) with regulators/ law enforcement agencies/ judicial institutions, in the financial year, in the following format (Note: The entity shall make disclosures on the basis of materiality as specified in Regulation 30 of SEBI (Listing Obligations and Disclosure Obligations) Regulations, 2015 and as disclosed on the entity's website):",
    "",
    { fullWidth: true }
  );

  mergeRow(ws, row, COL_D, COL_END, "Monetary", {
    bold: true,
    fill: TABLE_HEADER_FILL,
    align: "center",
  });
  row += 1;

  const monetaryRows = [
    ["Penalty/ Fine", PENALTY_OR_FINE_SHEET],
    ["Settlement", SETTLEMENT_SHEET],
    ["Compounding fee", COMPOUNDING_FEE_SHEET],
  ];

  monetaryRows.forEach(([label, sheetName]) => {
    setCell(ws, row, COL_D, label, { align: "left" });
    mergeRow(ws, row, COL_E, COL_END, showDetails ? "View Details" : "-", {
      align: "center",
      link: showDetails ? `#'${sheetName}'!D3` : undefined,
    });
    row += 1;
  });

  mergeRow(ws, row, COL_D, COL_END, "Non-Monetary", {
    bold: true,
    fill: TABLE_HEADER_FILL,
    align: "center",
  });
  row += 1;

  const nonMonetaryRows = [
    ["Imprisonment", IMPRISONMENT_SHEET],
    ["Punishment", PUNISHMENT_SHEET],
  ];

  nonMonetaryRows.forEach(([label, sheetName]) => {
    setCell(ws, row, COL_D, label, { align: "left" });
    mergeRow(ws, row, COL_E, COL_END, showDetails ? "View Details" : "-", {
      align: "center",
      link: showDetails ? `#'${sheetName}'!D3` : undefined,
    });
    row += 1;
  });

  return row;
}

function addDisciplinaryTable(ws, row, essential, fyCurrent, fyPrevious) {
  row = addQuestionRow(
    ws,
    row,
    "5. Number of Directors/KMPs/employees/workers against whom disciplinary action was taken by any law enforcement agency for the charges of bribery/ corruption:",
    "",
    { fullWidth: true }
  );

  setCell(ws, row, COL_D, "Segment", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  mergeRow(ws, row, COL_E, COL_F, fyCurrent, {
    bold: true,
    fill: TABLE_HEADER_FILL,
    align: "center",
  });
  mergeRow(ws, row, COL_G, COL_H, fyPrevious, {
    bold: true,
    fill: TABLE_HEADER_FILL,
    align: "center",
  });
  row += 1;

  const segments = [
    ["Board of Directors", "boardOfDirectors"],
    ["Key Management Personnel", "keyManagementPersonnel"],
    ["Employees other than BoD and KMPs", "employeesOtherThanBoDAndKMPs"],
    ["Workers", "workers"],
  ];

  segments.forEach(([label, key]) => {
    const values = disciplinaryValue(essential?.disciplinaryActions?.[key]);
    setCell(ws, row, COL_D, label, { align: "left" });
    mergeRow(ws, row, COL_E, COL_F, values.current, { align: "center" });
    mergeRow(ws, row, COL_G, COL_H, values.previous, { align: "center" });
    row += 1;
  });

  return row;
}

function addConflictTable(ws, row, essential, fyCurrent, fyPrevious) {
  row = addQuestionRow(
    ws,
    row,
    "6. Details of complaints with regard to conflict of interest:",
    "",
    { fullWidth: true }
  );

  setCell(ws, row, COL_D, "Segment", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  mergeRow(ws, row, COL_E, COL_F, fyCurrent, {
    bold: true,
    fill: TABLE_HEADER_FILL,
    align: "center",
  });
  mergeRow(ws, row, COL_G, COL_H, fyPrevious, {
    bold: true,
    fill: TABLE_HEADER_FILL,
    align: "center",
  });
  row += 1;

  setCell(ws, row, COL_D, "", { fill: TABLE_HEADER_FILL });
  setCell(ws, row, COL_E, "Number", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_F, "Remarks", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_G, "Number", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_H, "Remarks", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  row += 1;

  const conflict = essential?.conflictOfInterestComplaints || {};
  const rows = [
    ["directors", "Directors"],
    ["keyManagement", "KMPs"],
  ];

  rows.forEach(([key, label]) => {
    const labelText = `Number of complaints received in relation to issues of Conflict of Interest of the ${label}`;
    setCell(ws, row, COL_D, labelText, { align: "left", vertical: "top" });
    mergeRow(
      ws,
      row,
      COL_E,
      COL_E,
      getNumberOrNA(conflict?.currentFinancialYear?.[key]?.numberOfComplaints),
      { align: "center" }
    );
    setCell(
      ws,
      row,
      COL_F,
      safeText(conflict?.currentFinancialYear?.[key]?.remarks, "NA"),
      { align: "left" }
    );
    setCell(
      ws,
      row,
      COL_G,
      getNumberOrNA(conflict?.previousFinancialYear?.[key]?.numberOfComplaints),
      { align: "center" }
    );
    setCell(
      ws,
      row,
      COL_H,
      safeText(conflict?.previousFinancialYear?.[key]?.remarks, "NA"),
      { align: "left" }
    );
    setAutoRowHeight(ws, row, [{ text: labelText, width: 22 }]);
    row += 1;
  });

  return row;
}

function addAccountsPayablesTable(ws, row, essential, fyCurrent, fyPrevious) {
  row = addQuestionRow(
    ws,
    row,
    "8. Number of days of accounts payables [(Accounts payable*365)/ Cost of goods/services procured] in the following format:",
    "",
    { fullWidth: true }
  );

  setCell(ws, row, COL_D, "Parameter", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  mergeRow(ws, row, COL_E, COL_F, fyCurrent, {
    bold: true,
    fill: TABLE_HEADER_FILL,
    align: "center",
  });
  mergeRow(ws, row, COL_G, COL_H, fyPrevious, {
    bold: true,
    fill: TABLE_HEADER_FILL,
    align: "center",
  });
  row += 1;

  const ap = essential?.accountsPayables || {};
  const apRows = [
    ["i) Accounts payable x 365 days", "-", "-"],
    ["ii) Cost of goods/services procured", "-", "-"],
    [
      "iii) Number of days of accounts payables",
      getNumberOrNA(ap.currentFinancialYear),
      getNumberOrNA(ap.previousFinancialYear),
    ],
  ];

  apRows.forEach(([label, current, previous]) => {
    setCell(ws, row, COL_D, label, { align: "left" });
    mergeRow(ws, row, COL_E, COL_F, current, { align: "center" });
    mergeRow(ws, row, COL_G, COL_H, previous, { align: "center" });
    row += 1;
  });

  return row;
}

function addOpennessTable(ws, row, essential, fyCurrent, fyPrevious) {
  row = addQuestionRow(
    ws,
    row,
    "9. Open-ness of business - Provide details of concentration of purchases and sales with trading houses, dealers, and related parties along-with loans and advances & investments, with related parties:",
    "",
    { fullWidth: true }
  );

  setCell(ws, row, COL_D, "Parameter", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_E, "Metrics", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  mergeRow(ws, row, COL_F, COL_G, fyCurrent, {
    bold: true,
    fill: TABLE_HEADER_FILL,
    align: "center",
  });
  mergeRow(ws, row, COL_H, COL_I, fyPrevious, {
    bold: true,
    fill: TABLE_HEADER_FILL,
    align: "center",
  });
  row += 1;

  const rpt = essential?.relatedPartyTransactions || {};
  let lastParameter = "";

  OPENNESS_ROWS.forEach(({ parameter, metric, section, key }) => {
    if (parameter !== lastParameter) {
      lastParameter = parameter;
    }
    setCell(ws, row, COL_D, parameter, { align: "left", vertical: "top" });
    setCell(ws, row, COL_E, metric, { align: "left", vertical: "top" });
    mergeRow(
      ws,
      row,
      COL_F,
      COL_G,
      rptValue(rpt, section, key, "currentFinancialYear"),
      { align: "center" }
    );
    mergeRow(
      ws,
      row,
      COL_H,
      COL_I,
      rptValue(rpt, section, key, "previousFinancialYear"),
      { align: "center" }
    );
    setAutoRowHeight(ws, row, [{ text: metric, width: 30 }]);
    row += 1;
  });

  return row;
}

function addLeadershipAwarenessTable(ws, row) {
  row = addQuestionRow(
    ws,
    row,
    "1. Awareness programmes conducted for value chain partners on any of the Principles during the financial year:",
    "",
    { fullWidth: true }
  );

  setCell(ws, row, COL_D, "", { align: "left" });
  mergeRow(ws, row, COL_E, COL_END, "View Details", {
    align: "center",
    link: `#'${PROGRAMMES_FOR_VALUE_CHAIN_SHEET}'!D3`,
  });
  return row + 1;
}

export function buildPrinciple1Sheet(workbook, sectionC, reportingYear = "") {
  const ws = workbook.addWorksheet("PRINCIPLE 1", {
    views: [{ showGridLines: true }],
  });

  const widths = {
    A: 2,
    B: 2,
    C: 2,
    D: 28,
    E: 20,
    F: 14,
    G: 14,
    H: 14,
    I: 14,
    J: 14,
    K: 14,
    L: 14,
  };
  Object.entries(widths).forEach(([col, width]) => {
    ws.getColumn(col).width = width;
  });

  const principle = sectionC?.principle1 || {};
  const essential = principle?.essentialIndicators || {};
  const leadership = principle?.leadershipIndicator || {};
  const fyLabels = getFinancialYearLabels(reportingYear);
  const fyCurrent = fyLabels.currentYear ? `FY (${fyLabels.currentYear})` : "FY (Current Financial Year)";
  const fyPrevious = fyLabels.previousYear ? `PY (${fyLabels.previousYear})` : "PY (Previous Financial Year)";

  let row = 3;
  row = addSectionHeader(ws, row, "SECTION C: PRINCIPLE WISE PERFORMANCE DISCLOSURE");
  row = addQuestionRow(
    ws,
    row,
    "This section is aimed at helping entities demonstrate their performance in integrating the Principles and Core Elements with key processes and decisions. The information sought is categorized as “Essential” and “Leadership”. While the essential indicators are expected to be disclosed by every entity that is mandated to file this report, the leadership indicators may be voluntarily disclosed by entities which aspire to progress to a higher level in their quest to be socially, environmentally and ethically responsible.",
    "",
    { fullWidth: true }
  );
  row = addSectionHeader(
    ws,
    row,
    `PRINCIPLE 1 ${PRINCIPLE_TITLE}`
  );
  row = addSectionHeader(ws, row, "Essential Indicators", SUBHEADER_FILL);

  row = addTrainingTable(ws, row, essential?.percentageCoverageByTraining || {});
  row = addFineRows(ws, row, essential);

  row = addQuestionRow(
    ws,
    row,
    "3. Of the instances disclosed in Question 2 above, details of the Appeal/ Revision preferred in cases where monetary or non-monetary action has been appealed",
    essential?.detailsOfFine ? "View Details" : "-",
    {
      valueCol: COL_E,
      valueEnd: COL_END,
      valueAlign: "center",
      link: essential?.detailsOfFine
        ? `#'${APPEAL_OR_REVISION_CASE_SHEET}'!D3`
        : undefined,
    }
  );

  row = addQuestionRow(
    ws,
    row,
    "4. Does the entity have anti-corruption or anti-bribery policy? If yes, provide details in brief and if available, provide a web-link to the policy.",
    toYesNo(essential?.antiCorruptionPolicy?.hasPolicy),
    { valueCol: COL_E, valueEnd: COL_END, valueAlign: "center" }
  );

  if (essential?.antiCorruptionPolicy?.hasPolicy) {
    row = addQuestionRow(
      ws,
      row,
      "If Yes, provide details in brief.",
      safeText(essential?.antiCorruptionPolicy?.policyDetails, "View Details"),
      { valueCol: COL_E, valueEnd: COL_END, valueAlign: "left" }
    );
    row = addQuestionRow(
      ws,
      row,
      "Provide a web-link if the entity has anti-corruption or anti-bribery policy",
      safeText(essential?.antiCorruptionPolicy?.policyLink, "-"),
      { valueCol: COL_E, valueEnd: COL_END, valueAlign: "left", link: essential?.antiCorruptionPolicy?.policyLink || undefined }
    );
  }

  row = addDisciplinaryTable(ws, row, essential, fyCurrent, fyPrevious);
  row = addConflictTable(ws, row, essential, fyCurrent, fyPrevious);

  row = addQuestionRow(
    ws,
    row,
    "7. Provide details of any corrective action taken or underway on issues related to fines / penalties / action taken by regulators/ law enforcement agencies/ judicial institutions, on cases of corruption and conflicts of interest.",
    safeText(essential?.correctiveActions, "View Details"),
    { valueCol: COL_E, valueEnd: COL_END, valueAlign: "left" }
  );

  row = addAccountsPayablesTable(ws, row, essential, fyCurrent, fyPrevious);
  row = addOpennessTable(ws, row, essential, fyCurrent, fyPrevious);

  row = addSectionHeader(ws, row, "Leadership Indicators", SUBHEADER_FILL);
  row = addLeadershipAwarenessTable(ws, row);

  const conflictAnswer = leadership?.avoidManageConflict?.status
    ? `Yes${leadership?.avoidManageConflict?.remark ? ` - ${leadership.avoidManageConflict.remark}` : ""}`
    : "No";

  row = addQuestionRow(
    ws,
    row,
    "2. Does the entity have processes in place to avoid/ manage conflict of interests involving members of the Board? (Yes / No). If Yes, provide details of the same.",
    conflictAnswer,
    { valueCol: COL_E, valueEnd: COL_END, valueAlign: "left" }
  );

  if (leadership?.avoidManageConflict?.status && leadership?.avoidManageConflict?.remark) {
    addQuestionRow(
      ws,
      row,
      "If Yes, provide details of the same.",
      safeText(leadership?.avoidManageConflict?.remark, "View Details"),
      { valueCol: COL_E, valueEnd: COL_END, valueAlign: "left" }
    );
  }

  return ws;
}
